import i18n from 'src/lib/i18n/i18n';
import View from '@play-co/timestep-core/lib/ui/View';
import { Feature } from 'src/replicant/analytics';
import { Match2PowerupButton } from './Match2PowerupButton';
import { getPuzzleMap } from '../match2.assets';
import PopupMatch2Base, { PopupMatch2BaseInitOptions } from './PopupMatch2Base';
import { Match2GoalsSummary } from './Match2GoalsSummary';
import {
  match2Powerups,
  Match2PowerupType,
  powerupNames,
} from 'src/replicant/match2/match2.ruleset';
import { runEnergyCheckSequence } from '../match2.sequence';
import Match2PopupSubHeader from './Match2PopupSubHeader';
import Match2BuruStreakPanel from './Match2BuruStreakPanel';
import { getUrlSearchParamValue } from 'src/lib/utils';
import StateObserver, { user } from 'src/StateObserver';
import { isMatch2UnlockableUnlocked } from 'src/replicant/match2/match2.getters';
import { MapDef } from '../game/defs/map';
import { captureGenericError } from 'src/lib/sentry';
import {
  canPlayLeague,
  getCurrentLeaguePhase,
  hasJoinedLeague,
} from 'src/replicant/match2/leagues/leagues.getters';
import { getTutorialController } from 'src/game/tutorial/TutorialController';
import { OutOfCoins } from 'src/game/puzzlecoins/components/OutOfCoins';
import { puzzleCoinsIsPlayingTutorialDeterministic } from 'src/replicant/puzzlecoins/puzzlecoins.getters';
import { track } from 'src/lib/analytics/utils';

type CloseHandler = (result: {
  success: boolean;
  powerups?: Match2PowerupType[];
}) => void;

interface PopupMatch2PregameInitOptions extends PopupMatch2BaseInitOptions {
  level: number;
  origin?: string;
  coinsPinch?: boolean;
}

export default class PopupMatch2Pregame extends PopupMatch2Base {
  private goals: Match2GoalsSummary;
  private powerups: Match2PowerupButton[] = [];
  private streak?: Match2BuruStreakPanel;
  private level: number = 0;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close({ success: false }),
      title: {
        type: 'banner',
        color: hasJoinedLeague(user()) ? 'yellow' : 'green',
      },
      cta: {
        localeText: () => i18n('match2.ui.play').toLocaleUpperCase(),
        onClick: async () => {
          if (!(await runEnergyCheckSequence())) return;

          this.powerups.forEach((powerup) => {
            if (!powerup.isSelected) return;
            track('PowerupConsume', {
              feature: Feature.MATCH2.PREGAME_POPUP,
              powerupName: powerupNames[powerup.getType()],
              puzzleLevel: this.level,
              leaguePhase: canPlayLeague(user())
                ? getCurrentLeaguePhase(user())
                : undefined,
            });
          });

          return opts.close({
            success: true,
            powerups: this.powerups
              .filter((p) => p.isSelected)
              .map((p) => p.getType()),
          });
        },
        positioning: 'edge',
      },
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PREGAME_POPUP,
      },
    });

    this.goals = new Match2GoalsSummary({
      parent: this.panel,
      x: this.panel.style.width * 0.5,
      y: 150,
    });

    new Match2PopupSubHeader({
      superview: this.panel,
      x: this.panel.style.width * 0.5,
      y: this.panel.style.height * 0.5 - 12,
      localeText: () => i18n('match2.ui.selectBoosters'),
    });

    const width = this.panel.style.width * 0.45;
    const diff = this.panel.style.width - width;

    match2Powerups.forEach((type, index) => {
      const panel = new Match2PowerupButton();

      panel.setType(type);
      panel.updateOpts({
        x: width * 0.5 * index + diff * 0.5,
        y: this.panel.style.height - 150,
        centerOnOrigin: true,
      });

      this.panel.addSubview(panel);
      this.powerups.push(panel);
    });
  }

  private createBuruStreakPanel() {
    this.streak = new Match2BuruStreakPanel({
      superview: this.panel,
      x: this.panel.style.width * 0.5,
      y: this.panel.style.height + 190,
    });
  }

  public override async init({
    level,
    useClickOutside,
    origin,
    coinsPinch,
  }: PopupMatch2PregameInitOptions) {
    const testLevel = getUrlSearchParamValue('level');
    if (testLevel) level = Number(testLevel);

    this.level = level;
    const { user } = StateObserver.getState();

    if (canPlayLeague(user)) {
      this.setTitleText(() =>
        i18n('match2.leagues.phase', {
          phase: getCurrentLeaguePhase(user),
        }).toLocaleUpperCase(),
      );
    } else {
      this.setTitleText(() =>
        i18n('match2.ui.level', { level }).toLocaleUpperCase(),
      );
    }

    super.init({ useClickOutside });

    if (isMatch2UnlockableUnlocked(user, 'buruStreak')) {
      this.createBuruStreakPanel();
    }

    this.powerups.forEach((powerup) => {
      const type = powerup.getType();
      const user = StateObserver.getState().user;
      powerup.setLocked(!isMatch2UnlockableUnlocked(user, type));
      powerup.autoUpdate();
    });

    // Create a coins pinch widget if needed.
    if (coinsPinch) {
      this.createOutOfCoinsWidget();
    }

    // Disable close while playing tutorial, to prevent user skipping the step.
    if (puzzleCoinsIsPlayingTutorialDeterministic(user)) {
      this.buttonClose?.hide();
    }

    // Update level goals - requires puzzle maps to be loaded.
    await this.updateGoals(level);
  }

  private async updateGoals(level: number) {
    let map: MapDef | null = null;

    try {
      // Attempt to retrieve the map definition for the given level.
      map = await getPuzzleMap(level);
    } catch (e) {
      captureGenericError(`Match2PreGameError`, e);
    }

    // If map is not defined, probably it has failed to load, but we can still show the popup
    // as the game will try to load the map again when the user clicks on the play button.
    if (!map) return;

    // Set the map goals.
    this.goals.setGoals(map.goals);
  }

  public override async fadeIn() {
    void getTutorialController().handleEvent('match2PreGamePopupOpened');
    await super.fadeIn();
    this.streak?.show();
  }

  public override async fadeOut() {
    this.streak?.hide?.();
    await super.fadeOut();
    this.powerups.forEach((powerup) => {
      powerup.stopAutoUpdate();
    });
  }

  private createOutOfCoinsWidget() {
    new OutOfCoins({
      superview: this.panel,
      x: this.panel.style.width * 0.5,
      y: -130,
    });
  }
}
